import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, Briefcase, Loader2 } from 'lucide-react';
import { useTheme } from '../../core/contexts/ThemeContext';
import jsPDF from 'jspdf';
const PHOTO_URL = 'https://cdn.magicpatterns.com/uploads/6LWFciUpqXzvc9S9PJFRtx/Scherm%C2%ADafbeelding_2026-04-19_om_14.10.57.png';
function loadCircularImageAsBase64(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const size = Math.min(img.width, img.height);
      const sourceX = (img.width - size) / 2;
      const sourceY = Math.max(0, (img.height - size) * 0.12);
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas context failed'));
        return;
      }
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(img, sourceX, sourceY, size, size, 0, 0, size, size);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = url;
  });
}
function toPdfText(value: string): string {
  return value.replace(/[–—]/g, '-').replace(/·/g, '/');
}
async function generateCV(t: (key: string) => string) {
  const doc = new jsPDF({
    unit: 'mm',
    format: 'a4'
  });
  var pageW = doc.internal.pageSize.getWidth();
  var pageH = doc.internal.pageSize.getHeight();
  var sidebarW = 62;
  var mainX = sidebarW + 12;
  var mainW = pageW - mainX - 16;
  var sidebarPad = 10;
  var sidebarBg = [26, 26, 26] as const;
  var copper = [200, 149, 108] as const;
  var white = [255, 255, 255] as const;
  var lightGray = [190, 190, 188] as const;
  var dark = [26, 26, 26] as const;
  var muted = [96, 96, 94] as const;
  var cardBg = [249, 249, 247] as const;
  var drawSectionLabel = function (label: string, y: number) {
    doc.setDrawColor(84, 78, 70);
    doc.setLineWidth(0.25);
    doc.line(sidebarPad, y + 2.2, sidebarW - sidebarPad, y + 2.2);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.setCharSpace(0.8);
    doc.setTextColor(...copper);
    doc.text(label, sidebarPad, y);
    doc.setCharSpace(0);
  };
  doc.setFillColor(...sidebarBg);
  doc.rect(0, 0, sidebarW, pageH, 'F');
  doc.setFillColor(...copper);
  doc.rect(0, 0, sidebarW, 2.4, 'F');
  var sY = 17;
  try {
    var imgData = await loadCircularImageAsBase64(PHOTO_URL);
    var photoSize = 35;
    var photoX = (sidebarW - photoSize) / 2;
    var photoCenter = photoX + photoSize / 2;
    doc.addImage(imgData, 'PNG', photoX, sY, photoSize, photoSize);
    doc.setDrawColor(...copper);
    doc.setLineWidth(0.35);
    doc.circle(photoCenter, sY + photoSize / 2, photoSize / 2 + 0.7, 'S');
    sY += photoSize + 9;
  } catch (error) {
    sY += 12;
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...white);
  doc.text('Mick van Latum', sidebarW / 2, sY, {
    align: 'center'
  });
  sY += 5.5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.05);
  doc.setCharSpace(0.32);
  doc.setTextColor(...copper);
  doc.text(t('home.role_pdf').toUpperCase(), sidebarW / 2, sY, {
    align: 'center'
  });
  doc.setCharSpace(0);
  sY += 13;
  drawSectionLabel('CONTACT', sY);
  sY += 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.8);
  doc.setTextColor(...lightGray);
  var contactItems = ['mick@mvlcreative.com', '065 124 4475', 'Almere, Nederland', 'linkedin.com/in/mick-van-latum'];
  contactItems.forEach(function (item) {
    doc.text(item, sidebarPad, sY);
    sY += 5.35;
  });
  sY += 7;
  drawSectionLabel('EXPERTISE', sY);
  sY += 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.15);
  doc.setTextColor(...lightGray);
  var skills = [['Product Design', 'UX Research'], ['Design Systems', 'Frontend Dev'], ['LLM Integraties', 'Prompt Engineering'], ['React / TypeScript', 'Wireframing']];
  skills.forEach(function (row) {
    doc.text(row[0], sidebarPad, sY);
    doc.text(row[1], sidebarPad + 22, sY);
    sY += 5.1;
  });
  sY += 7;
  drawSectionLabel('TOOLS', sY);
  sY += 8;
  doc.setFontSize(6.6);
  doc.setTextColor(...lightGray);
  var tools = ['Figma', 'VS Code', 'Adobe CC', 'Magic Patterns'];
  tools.forEach(function (tool) {
    doc.text(tool, sidebarPad, sY);
    sY += 5.15;
  });
  sY += 7;
  drawSectionLabel('TALEN', sY);
  sY += 8;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.45);
  doc.setTextColor(...lightGray);
  doc.text('Nederlands', sidebarPad, sY);
  doc.text('English', sidebarPad + 22, sY);
  sY += 4;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.7);
  doc.setTextColor(142, 142, 140);
  doc.text('Moedertaal', sidebarPad, sY);
  doc.text('C1 professioneel', sidebarPad + 22, sY);
  sY += 10;
  doc.setDrawColor(84, 78, 70);
  doc.setLineWidth(0.25);
  doc.line(sidebarPad, pageH - 25, sidebarW - sidebarPad, pageH - 25);
  doc.setFillColor(70, 66, 62);
  for (var gx = sidebarPad; gx < sidebarW - sidebarPad; gx += 3.5) {
    for (var gy = pageH - 20; gy < pageH - 13; gy += 3.5) {
      doc.circle(gx, gy, 0.25, 'F');
    }
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6);
  doc.setTextColor(125, 119, 112);
  doc.text('MVLCREATIVE', sidebarPad, pageH - 8);

  // ===== MAIN CONTENT =====
  var mY = 20;
  doc.setFillColor(...copper);
  doc.rect(sidebarW, 0, pageW - sidebarW, 2.4, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.2);
  doc.setCharSpace(1);
  doc.setTextColor(...copper);
  doc.text('PROFIEL', mainX, mY);
  doc.setCharSpace(0);
  mY += 7;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.4);
  doc.setTextColor(...muted);
  var pitchLines = doc.splitTextToSize(toPdfText(t('home.pitch')), mainW);
  doc.text(pitchLines, mainX, mY);
  mY += pitchLines.length * 5 + 9;
  doc.setDrawColor(226, 218, 210);
  doc.setLineWidth(0.35);
  doc.line(mainX, mY, mainX + mainW, mY);
  doc.setFillColor(...copper);
  doc.circle(mainX + mainW / 2, mY, 1.3, 'F');
  mY += 12;
  var drawExperienceHeading = function (label: string, y: number) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.2);
    doc.setCharSpace(1);
    doc.setTextColor(...copper);
    doc.text(label.toUpperCase(), mainX, y);
    doc.setCharSpace(0);
  };
  drawExperienceHeading(t('resume.experience'), mY);
  mY += 10;
  var timelineX = mainX + 3;
  var contentX = mainX + 11;
  var cardX = mainX + 8.5;
  var cardW = mainW - 8.5;
  var jobs = [{
    role: toPdfText(t('resume.freelance_role')),
    company: toPdfText(t('resume.freelance_company')),
    period: toPdfText(t('resume.freelance_period')),
    description: toPdfText(t('resume.freelance_description')),
    current: true
  }, {
    role: toPdfText(t('resume.webheld_role')),
    company: toPdfText(t('resume.webheld_company')),
    period: toPdfText(t('resume.webheld_period')),
    description: toPdfText(t('resume.webheld_description')),
    current: false
  }, {
    role: toPdfText(t('resume.aim_role')),
    company: toPdfText(t('resume.aim_company')),
    period: toPdfText(t('resume.aim_period')),
    description: toPdfText(t('resume.aim_description')),
    current: false
  }, {
    role: toPdfText(t('resume.tweak_role')),
    company: toPdfText(t('resume.tweak_company')),
    period: toPdfText(t('resume.tweak_period')),
    description: toPdfText(t('resume.tweak_description')),
    current: false
  }];
  var drawFooter = function (x: number, width: number) {
    doc.setDrawColor(...copper);
    doc.setLineWidth(0.35);
    doc.line(x, pageH - 16, x + width, pageH - 16);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.2);
    doc.setTextColor(...copper);
    doc.text('mvlcreative.com', x, pageH - 10.5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.2);
    doc.setTextColor(142, 142, 140);
    doc.text('Portfolio / Product Design & Development', x, pageH - 6.3);
  };
  var startExperiencePage = function () {
    drawFooter(mainX, mainW);
    doc.addPage();
    doc.setFillColor(...white);
    doc.rect(0, 0, pageW, pageH, 'F');
    doc.setFillColor(...copper);
    doc.rect(0, 0, pageW, 2.4, 'F');
    mY = 22;
    drawExperienceHeading(t('resume.experience') + ' / vervolg', mY);
    mY += 10;
  };
  jobs.forEach(function (job, index) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.7);
    var descriptionLines = doc.splitTextToSize(job.description, cardW - 9);
    var cardH = 18 + descriptionLines.length * 3.9;
    if (mY + cardH > pageH - 20) {
      startExperiencePage();
    }
    var cardY = mY;
    doc.setFillColor(...cardBg);
    doc.roundedRect(cardX, cardY - 2, cardW, cardH, 2.5, 2.5, 'F');
    doc.setFillColor(...copper);
    doc.roundedRect(cardX, cardY - 2, 1.2, cardH, 0.6, 0.6, 'F');
    if (job.current) {
      doc.setFillColor(...copper);
      doc.circle(timelineX, cardY + 0.5, 1.7, 'F');
    } else {
      doc.setFillColor(...white);
      doc.setDrawColor(...copper);
      doc.setLineWidth(0.4);
      doc.circle(timelineX, cardY + 0.5, 1.4, 'FD');
    }
    if (index < jobs.length - 1) {
      doc.setDrawColor(220, 210, 201);
      doc.setLineWidth(0.3);
      doc.line(timelineX, cardY + 3.5, timelineX, cardY + cardH - 3);
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.4);
    doc.setTextColor(...dark);
    doc.text(job.role, contentX, cardY + 1.5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.4);
    doc.setTextColor(...copper);
    doc.text(job.period, cardX + cardW - 4, cardY + 1.5, {
      align: 'right'
    });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.9);
    doc.setTextColor(...muted);
    doc.text(job.company, contentX, cardY + 6.5);
    doc.setDrawColor(229, 229, 226);
    doc.setLineWidth(0.25);
    doc.line(contentX, cardY + 9.2, cardX + cardW - 4, cardY + 9.2);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.7);
    doc.setTextColor(...muted);
    doc.text(descriptionLines, contentX, cardY + 14);
    mY += cardH + 4.5;
  });
  var learningText = toPdfText(t('resume.learning_focus'));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.8);
  var learningBodyLines = doc.splitTextToSize(learningText, cardW - 9);
  var learningCardH = 17 + learningBodyLines.length * 4;
  if (mY + learningCardH + 6 > pageH - 18) {
    drawFooter(mainX, mainW);
    doc.addPage();
    doc.setFillColor(...white);
    doc.rect(0, 0, pageW, pageH, 'F');
    doc.setFillColor(...copper);
    doc.rect(0, 0, pageW, 2.4, 'F');
    mY = 22;
  }
  doc.setDrawColor(226, 218, 210);
  doc.setLineWidth(0.3);
  doc.line(mainX, mY, mainX + mainW, mY);
  doc.setFillColor(...copper);
  doc.circle(mainX + mainW / 2, mY, 1.1, 'F');
  mY += 6;
  drawExperienceHeading(t('resume.learning'), mY);
  mY += 6;
  doc.setFillColor(...cardBg);
  doc.roundedRect(cardX, mY - 2, cardW, learningCardH, 2.5, 2.5, 'F');
  doc.setFillColor(...copper);
  doc.roundedRect(cardX, mY - 2, 1.2, learningCardH, 0.6, 0.6, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...dark);
  doc.text(toPdfText(t('resume.learning_practice_title')), contentX, mY + 2);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.8);
  doc.setTextColor(...muted);
  doc.text(learningBodyLines, contentX, mY + 8);
  mY += learningCardH + 4.5;
  drawFooter(mainX, mainW);
  doc.addPage();
  doc.setFillColor(...white);
  doc.rect(0, 0, pageW, pageH, 'F');
  doc.setFillColor(...copper);
  doc.rect(0, 0, pageW, 2.4, 'F');
  var projectX = 18;
  var projectW = pageW - projectX * 2;
  var projectY = 26;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.2);
  doc.setCharSpace(1);
  doc.setTextColor(...copper);
  doc.text(toPdfText(t('resume.projects')).toUpperCase(), projectX, projectY);
  doc.setCharSpace(0);
  projectY += 16;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(25);
  doc.setTextColor(...dark);
  doc.text(toPdfText(t('resume.project_meester_title')), projectX, projectY);
  projectY += 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...muted);
  doc.text(toPdfText(t('resume.project_meester_subtitle')), projectX, projectY);
  projectY += 8;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...copper);
  doc.text('meesterdjay.nl', projectX, projectY);
  doc.text(toPdfText(t('resume.project_meester_meta')), projectX + projectW, projectY, {
    align: 'right'
  });
  projectY += 10;
  doc.setDrawColor(226, 218, 210);
  doc.setLineWidth(0.35);
  doc.line(projectX, projectY, projectX + projectW, projectY);
  projectY += 13;
  doc.setFillColor(...cardBg);
  doc.roundedRect(projectX, projectY, projectW, 57, 3, 3, 'F');
  doc.setFillColor(...copper);
  doc.roundedRect(projectX, projectY, 1.5, 57, 0.7, 0.7, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.2);
  doc.setTextColor(...dark);
  doc.text('PRODUCT CASE', projectX + 8, projectY + 11);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.2);
  doc.setTextColor(...muted);
  var projectDescriptionLines = doc.splitTextToSize(toPdfText(t('resume.project_meester_description')), projectW - 16);
  doc.text(projectDescriptionLines, projectX + 8, projectY + 20);
  doc.setDrawColor(229, 229, 226);
  doc.setLineWidth(0.25);
  doc.line(projectX + 8, projectY + 35, projectX + projectW - 8, projectY + 35);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.4);
  doc.setTextColor(...copper);
  doc.text(toPdfText(t('resume.project_meester_metric_subjects')), projectX + 8, projectY + 44);
  doc.text(toPdfText(t('resume.project_meester_metric_questions')), projectX + projectW / 2, projectY + 44, {
    align: 'center'
  });
  doc.text(toPdfText(t('resume.project_meester_metric_levels')), projectX + projectW - 8, projectY + 44, {
    align: 'right'
  });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.8);
  doc.setTextColor(...muted);
  var capabilityLines = doc.splitTextToSize(toPdfText(t('resume.project_meester_capability')), projectW);
  doc.text(capabilityLines, projectX, projectY + 72);
  drawFooter(projectX, projectW);
  doc.save('CV_Mick_van_Latum.pdf');
}
export function ResumeApp() {
  const {
    theme,
    t
  } = useTheme();
  const isLightTheme = theme === 'light';
  const [isGenerating, setIsGenerating] = useState(false);
  const handleDownload = async () => {
    setIsGenerating(true);
    try {
      await generateCV(t);
    } finally {
      setIsGenerating(false);
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 28
      }
    }
  };
  const experiences = [{
    role: t('resume.freelance_role'),
    company: t('resume.freelance_company'),
    period: t('resume.freelance_period'),
    description: t('resume.freelance_description'),
    highlight: true
  }, {
    role: t('resume.webheld_role'),
    company: t('resume.webheld_company'),
    period: t('resume.webheld_period'),
    description: t('resume.webheld_description')
  }, {
    role: t('resume.aim_role'),
    company: t('resume.aim_company'),
    period: t('resume.aim_period'),
    description: t('resume.aim_description')
  }, {
    role: t('resume.tweak_role'),
    company: t('resume.tweak_company'),
    period: t('resume.tweak_period'),
    description: t('resume.tweak_description')
  }];
  return <div className="h-full overflow-y-auto p-5 md:p-16 pb-28" data-id="element-71" data-mp="1vt6doe1k3tlo">
      <motion.div className="max-w-3xl mx-auto space-y-12 md:space-y-16" variants={containerVariants} initial="hidden" animate="visible" data-id="element-72" data-mp="1ztymj66inwhf">
        {/* Header & Download */}
        <motion.div variants={itemVariants} className={`flex flex-col sm:flex-row sm:items-end justify-between gap-6 pb-8 border-b ${isLightTheme ? 'border-black/[0.06]' : 'border-white/[0.06]'}`} data-id="element-73" data-mp="2sc4bdoyuoxal">
          <div className="space-y-2" data-id="element-74" data-mp="2y3lje7qugi8c">
            <h1 className={`text-4xl md:text-5xl font-bold tracking-tight ${isLightTheme ? 'text-[#1A1A1A]' : 'text-[#E8E8E6]'}`} data-id="element-75" data-mp="2t63k792mphdj">
              Mick van Latum
            </h1>
            <p className={`font-mono text-sm uppercase tracking-widest ${isLightTheme ? 'text-[#6B6B6B]' : 'text-[#9B9B9B]'}`} data-id="element-76" data-mp="2j8yy198s9lwq">
              {t('home.role')}
            </p>
          </div>
          <button onClick={handleDownload} disabled={isGenerating} className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#C8956C] text-white font-medium hover:bg-[#B5825A] transition-colors shadow-lg shadow-[#C8956C]/20 disabled:opacity-60" data-id="element-77" data-mp="2ah6nyzno8eso">
            {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-78" data-mp="1vkxqa2jfi9id" /> : <Download className="w-4 h-4" data-id="element-79" data-mp="1leebu0qqdqy8" />}
            {isGenerating ? 'Generating...' : t('resume.download')}
          </button>
        </motion.div>

        {/* Experience */}
        <motion.div variants={itemVariants} className="space-y-8" data-id="element-80" data-mp="1vdn4mq9v0zj0">
          <div className="flex items-center gap-3" data-id="element-81" data-mp="20cq7q56a4voc">
            <Briefcase className={`w-5 h-5 ${isLightTheme ? 'text-[#1A1A1A]' : 'text-[#E8E8E6]'}`} data-id="element-82" data-mp="20a43aeeoqwac" />
            <h2 className={`text-2xl font-bold ${isLightTheme ? 'text-[#1A1A1A]' : 'text-[#E8E8E6]'}`} data-id="element-83" data-mp="17n9mpskzoh25">
              {t('resume.experience')}
            </h2>
          </div>

          <div className="space-y-4" data-id="element-84" data-mp="1hd2mzxfol9r3">
            {experiences.map((exp, i) => <motion.div key={i} variants={itemVariants} className={`p-6 md:p-8 rounded-xl border backdrop-blur-sm shadow-sm ${exp.highlight ? isLightTheme ? 'bg-white/50 border-[#C8956C]/20 border-l-2 border-l-[#C8956C]' : 'bg-[#1A1A1A]/50 border-[#C8956C]/20 border-l-2 border-l-[#C8956C]' : isLightTheme ? 'bg-white/30 border-black/[0.06]' : 'bg-[#1A1A1A]/30 border-white/[0.06]'}`} data-id="element-85" data-mp="jxyh9xsp1yho" data-mpi={i}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3" data-id="element-86" data-mp="2d2h0aw45ya4j">
                  <h3 className={`font-bold text-lg ${isLightTheme ? 'text-[#1A1A1A]' : 'text-[#E8E8E6]'}`} data-id="element-87" data-mp="28101r795izr4">
                    {exp.role}
                  </h3>
                  <span className="font-mono text-xs text-[#C8956C]" data-id="element-88" data-mp="2i094aoi31mp3">
                    {exp.period}
                  </span>
                </div>
                <div className={`font-medium mb-3 text-sm ${isLightTheme ? 'text-[#6B6B6B]' : 'text-[#9B9B9B]'}`} data-id="element-89" data-mp="2qtcemnxfljfi">
                  {exp.company}
                </div>
                <p className={`text-base leading-relaxed ${isLightTheme ? 'text-[#6B6B6B]' : 'text-[#9B9B9B]'}`} data-id="element-90" data-mp="30ribus8am1e1">
                  {exp.description}
                </p>
              </motion.div>)}
          </div>
        </motion.div>
      </motion.div>
    </div>;
}